import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import MovieCard from '../components/MovieCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Present = () => {
  const { t } = useTranslation();
  const [movies, setMovies] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMovies();
  }, [page]);

  const fetchMovies = async () => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      const response = await axios.get(
        `${API}/movies/discover?page=${page}&sort_by=popularity.desc&primary_release_date_gte=1980-01-01&primary_release_date_lte=${currentYear}-12-31`
      );
      setMovies(response.data.results || []);
    } catch (error) {
      console.error('Error fetching movies:', error);
      toast.error('Failed to load movies');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#7FFF7F] mx-auto mb-4"></div>
          <p className="text-gray-400">{t('browse.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 max-w-7xl mx-auto pb-20" data-testid="present-page">
      <div className="mb-12">
        <h1 className="font-anton text-5xl md:text-7xl text-white uppercase tracking-tight mb-4">
          {t('eras.presentTitle')}
        </h1>
        <p className="text-base md:text-lg text-gray-400">
          {t('eras.presentSubtitle')}
        </p>
      </div>

      {movies.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 mb-12">
            {movies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>

          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              className="border-white/20 hover:bg-white/10 text-white"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              {t('browse.previous')}
            </Button>
            <span className="text-white font-semibold px-6">
              {t('browse.page')} {page}
            </span>
            <Button
              variant="outline"
              className="border-white/20 hover:bg-white/10 text-white"
              onClick={() => setPage(page + 1)}
            >
              {t('browse.next')}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </>
      ) : (
        <div className="text-center py-20">
          <p className="text-gray-400 text-lg">{t('browse.noMovies')}</p>
        </div>
      )}
    </div>
  );
};

export default Present;